import { motion } from "framer-motion";
import { Brain, Zap, Trophy, Users, ChevronRight, Rocket, Target, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Always use window.location.origin to derive the redirect URL dynamically
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-foreground">Архітектор Майбутнього</span>
              </div>
              <Button 
                onClick={handleLogin}
                data-testid="login-button"
                className="bg-primary hover:bg-primary/90 text-white h-11 px-8 rounded-md font-medium transition-all duration-200 shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]"
              >
                Увійти
              </Button>
            </div>
          </div>
        </nav>

        <section className="py-20 md:py-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-block mb-6 px-4 py-2 rounded-full border border-primary/30 bg-primary/10">
                <span className="text-sm font-medium text-primary uppercase tracking-wider">Гейміфіковане навчання</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none text-foreground mb-6">
                Стань майстром{" "}
                <span className="text-gradient">архітектури</span>
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8 max-w-2xl mx-auto">
                Інтерактивна платформа для вивчення архітектури програмного забезпечення. Розвивай навички, приймай рішення та змагайся з кращими.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  onClick={handleLogin}
                  data-testid="hero-cta-button"
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white h-14 px-10 text-lg rounded-md font-medium transition-all duration-200 shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] group"
                >
                  Почати грати
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  className="h-14 px-10 text-lg border-border bg-card hover:bg-card/80"
                >
                  Дізнатися більше
                </Button>
              </div>
            </motion.div>
          </div>
          
          <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/30 rounded-full blur-3xl"></div>
          </div>
        </section>

        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: Rocket, title: "Швидкий квіз", desc: "Перевір знання принципів та паттернів" },
                { icon: Target, title: "Сценарії", desc: "Вирішуй реальні архітектурні проблеми" },
                { icon: Zap, title: "Trade-offs", desc: "Балансуй складність та продуктивність" },
                { icon: Trophy, title: "Дизайн систем", desc: "Проектуй комплексні системи" }
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-8 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="mb-4 w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.desc}</p>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
                Чому Архітектор Майбутнього?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Навчання через гру з AI-підтримкою
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Brain,
                  title: "AI-генерація питань",
                  desc: "Динамічні сценарії на основі книг про архітектуру"
                },
                {
                  icon: Users,
                  title: "Таблиця лідерів",
                  desc: "Змагайся з колегами та кращими архітекторами"
                },
                {
                  icon: Award,
                  title: "Система досягнень",
                  desc: "Розблоковуй значки та ставай майстром"
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.15, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl hover:border-primary/30 transition-all"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
                    <item.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-border/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
                Готовий почати свій шлях?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Приєднуйся до спільноти архітекторів та розвивай свої навички
              </p>
              <Button 
                onClick={handleLogin}
                data-testid="cta-button"
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white h-14 px-10 text-lg rounded-md font-medium transition-all duration-200 shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] group"
              >
                Почати зараз
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </section>

        <footer className="border-t border-border/50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-foreground">Архітектор Майбутнього</span>
              </div>
              <div className="text-center md:text-right">
                <p className="text-sm text-muted-foreground">© 2026. Всі права захищені.</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Автор ідеї: Євген Міхальов</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}